/**
 * Blog Related Pages
 * BlogPage - List of blog posts with submission form
 * BlogPostPage - Individual blog post view
 * BlogSubmitPage - Dedicated submission page
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  BookOpen, Calendar, Eye, ArrowLeft, Search, Tag, PenLine, 
  User, Mail, Image, Send, CheckCircle 
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadPosts();
    loadCategories();
  }, []);

  const loadPosts = async () => {
    try {
      const res = await axios.get(`${API}/api/blog`);
      setPosts(res.data);
    } catch {
      toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/blog/categories`);
      setCategories(res.data);
    } catch {
      // Ignore - categories are optional
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen pt-36 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50" data-testid="blog-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">CatPals+ Blog</h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg mb-6">
            Stories, tips, and updates from our cat rescue journey
          </p>
          <Button 
            onClick={() => navigate("/blog/submit")}
            className="bg-amber-600 hover:bg-amber-700 rounded-full"
            data-testid="write-post-btn"
          >
            <PenLine className="w-4 h-4 mr-2" />
            Write a Blog Post
          </Button>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              placeholder="Search articles..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === "" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory("")}
                className="rounded-full"
              >
                All
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className="rounded-full"
                >
                  {cat}
                </Button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
            <p className="mt-4 text-stone-600">Loading articles...</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-stone-700 mb-2">No articles found</h3>
            <p className="text-stone-500">
              {searchTerm || selectedCategory ? "Try adjusting your search or filter" : "Check back soon for new content!"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map(post => (
              <Card 
                key={post.post_id} 
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => navigate(`/blog/${post.slug}`)}
                data-testid={`blog-post-${post.slug}`}
              >
                {post.featured_image && (
                  <div className="aspect-video relative overflow-hidden bg-stone-100">
                    <img 
                      src={post.featured_image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="outline" className="text-xs border-amber-200 text-amber-700">
                      {post.category}
                    </Badge>
                    <span className="text-xs text-stone-400 flex items-center gap-1">
                      <Eye className="w-3 h-3" /> {post.views || 0}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-stone-900 mb-2 group-hover:text-amber-600 transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-stone-600 text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                  <div className="flex items-center text-xs text-stone-400">
                    <Calendar className="w-3 h-3 mr-1" />
                    {formatDate(post.created_at)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPost();
  }, [slug]);

  const loadPost = async () => {
    try {
      const res = await axios.get(`${API}/api/blog/${slug}`);
      setPost(res.data);
    } catch {
      toast.error("Article not found");
      navigate("/blog");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-36 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen pt-36 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50" data-testid="blog-post-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate("/blog")}
          className="mb-6 text-stone-600 hover:text-amber-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
        </Button>

        <article>
          {post.featured_image && (
            <div className="aspect-video relative overflow-hidden rounded-2xl mb-8 bg-stone-100">
              <img 
                src={post.featured_image} 
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                {post.category}
              </Badge>
              <span className="text-sm text-stone-400 flex items-center gap-1">
                <Calendar className="w-4 h-4" /> {formatDate(post.created_at)}
              </span>
              <span className="text-sm text-stone-400 flex items-center gap-1">
                <Eye className="w-4 h-4" /> {post.views || 0} views
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">{post.title}</h1>
            <p className="text-lg text-stone-600">{post.excerpt}</p>
          </header>

          {/* Render content as plain text with preserved line breaks for community submissions */}
          {post.author_type ? (
            <div className="prose prose-stone prose-lg max-w-none whitespace-pre-wrap">
              {post.content}
            </div>
          ) : (
            <div 
              className="prose prose-stone prose-lg max-w-none prose-headings:text-stone-900 prose-a:text-amber-600"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
            />
          )}

          {post.tags && post.tags.length > 0 && (
            <footer className="mt-8 pt-8 border-t border-stone-200">
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="w-4 h-4 text-stone-400" />
                {post.tags.map(tag => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </footer>
          )}
        </article>

        <div className="mt-12 p-6 bg-amber-50 rounded-2xl text-center">
          <h3 className="text-xl font-bold text-stone-900 mb-2">Enjoyed this article?</h3>
          <p className="text-stone-600 mb-4">Subscribe to our newsletter for more updates!</p>
          <Button onClick={() => navigate("/")} className="bg-amber-600 hover:bg-amber-700 rounded-full">
            Subscribe Now
          </Button>
        </div>
      </div>
    </div>
  );
};


// Blog Submission Page
const BlogSubmitPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    category: "Cat Care Tips",
    author_name: "",
    author_email: "",
    author_type: "customer",
    image_base64: ""
  });

  const categories = [
    "Cat Care Tips",
    "Adoption Stories",
    "Rescue Updates",
    "Health & Wellness",
    "Volunteer Experiences",
    "Community News",
    "Other"
  ];

  const authorTypes = [
    { value: "customer", label: "Customer / Cat Owner" },
    { value: "volunteer", label: "Volunteer" },
    { value: "staff", label: "Staff Member" }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image_base64: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.content.trim() || !formData.author_name.trim() || !formData.author_email.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/blog/submit`, formData);
      setSubmitted(true);
      toast.success("Your blog post has been submitted for review!");
    } catch (err) {
      toast.error("Failed to submit blog post. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-36 bg-gradient-to-br from-green-50 via-stone-50 to-amber-50 flex items-center justify-center" data-testid="blog-submit-success">
        <Card className="max-w-md mx-4 text-center">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-stone-900 mb-4">Thank You!</h1>
            <p className="text-stone-600 mb-6">
              Your blog post has been submitted for review. Our team will review it and, 
              if approved, it will appear on our blog soon!
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate("/blog")} className="w-full bg-amber-600 hover:bg-amber-700 rounded-full">
                Back to Blog
              </Button>
              <Button onClick={() => { setSubmitted(false); setFormData({ title: "", content: "", category: "Cat Care Tips", author_name: "", author_email: "", author_type: "customer", image_base64: "" }); }} variant="outline" className="w-full rounded-full">
                Submit Another Post
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-36 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50" data-testid="blog-submit-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate("/blog")}
          className="mb-6 text-stone-600 hover:text-amber-600"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
        </Button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PenLine className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2">Write a Blog Post</h1>
          <p className="text-stone-600">
            Share your cat stories, tips, or experiences with our community!
          </p>
        </div>

        <Card>
          <CardContent className="p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Author Info Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="author_name" className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4" /> Your Name *
                  </Label>
                  <Input
                    id="author_name"
                    name="author_name"
                    value={formData.author_name}
                    onChange={handleInputChange}
                    placeholder="Jane Smith"
                    required
                    data-testid="author-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="author_email" className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4" /> Your Email *
                  </Label>
                  <Input
                    id="author_email"
                    name="author_email"
                    type="email"
                    value={formData.author_email}
                    onChange={handleInputChange}
                    placeholder="jane@example.com"
                    required
                    data-testid="author-email-input"
                  />
                </div>
              </div>

              {/* Author Type */}
              <div>
                <Label className="mb-2 block">I am a...</Label>
                <div className="flex flex-wrap gap-2">
                  {authorTypes.map(type => (
                    <Button
                      key={type.value}
                      type="button"
                      variant={formData.author_type === type.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, author_type: type.value }))}
                      className={`rounded-full ${formData.author_type === type.value ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                      data-testid={`author-type-${type.value}`}
                    >
                      {type.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Post Title */}
              <div>
                <Label htmlFor="title" className="mb-2 block">Post Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="My Amazing Cat Adoption Story"
                  required
                  className="text-lg"
                  data-testid="post-title-input"
                />
              </div>

              {/* Category */}
              <div>
                <Label className="mb-2 block">Category</Label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <Button
                      key={cat}
                      type="button"
                      variant={formData.category === cat ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, category: cat }))}
                      className={`rounded-full ${formData.category === cat ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                    >
                      {cat}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div>
                <Label htmlFor="content" className="mb-2 block">Your Story *</Label>
                <Textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  placeholder="Write your blog post here. Share your story, tips, or experiences with our cat-loving community..."
                  rows={12}
                  required
                  className="resize-none"
                  data-testid="post-content-input"
                />
                <p className="text-xs text-stone-500 mt-1">
                  {formData.content.length} characters
                </p>
              </div>

              {/* Image Upload */}
              <div>
                <Label htmlFor="image" className="flex items-center gap-2 mb-2">
                  <Image className="w-4 h-4" /> Featured Image (Optional)
                </Label>
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="cursor-pointer"
                  data-testid="image-upload-input"
                />
                <p className="text-xs text-stone-500 mt-1">Max 5MB. JPG, PNG, or GIF.</p>
                {formData.image_base64 && (
                  <div className="mt-3 relative">
                    <img 
                      src={formData.image_base64} 
                      alt="Preview" 
                      className="w-full max-h-48 object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData(prev => ({ ...prev, image_base64: "" }))}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="pt-4 border-t">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-600 hover:bg-amber-700 rounded-full text-lg py-6"
                  data-testid="submit-post-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Submit for Review
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-stone-500 mt-3">
                  All submissions are reviewed by our team before publishing.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export { BlogPage, BlogPostPage, BlogSubmitPage };
